// import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNavigationHelpers } from '@/utils/navigationHelpers';
import {
  Home,
  ChevronRight,
  Users,
  Smartphone,
  BarChart3,
  FileInput,
  UserPlus,
  Edit,
  UserCog,
  SmartphoneIcon,
  Users2,
  Package,
  FileUp,
  Settings,
} from 'lucide-react';

interface BreadcrumbItem {
  label: string;
  path: string;
  icon?: React.ReactNode;
}

export const Breadcrumbs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { navigateWithTokenCheck } = useNavigationHelpers();

  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [];

    // Always start with Home
    breadcrumbs.push({
      label: t('common.home', 'Inicio'),
      path: '/menu',
      icon: <Home className="h-4 w-4" />,
    });

    // Handle different route patterns
    if (pathSegments[0] === 'users') {
      // Users routes
      breadcrumbs.push({
        label: t('pages.users.title'),
        path: '/users',
        icon: <Users className="h-4 w-4" />,
      });

      if (pathSegments[1] === 'new') {
        breadcrumbs.push({
          label: t('pages.users.createNew'),
          path: location.pathname,
          icon: <UserPlus className="h-4 w-4" />,
        });
      } else if (pathSegments[1] === 'edit' && pathSegments[2]) {
        breadcrumbs.push({
          label: t('pages.users.editUser'),
          path: location.pathname,
          icon: <Edit className="h-4 w-4" />,
        });
      } else if (pathSegments[2] === 'devices') {
        breadcrumbs.push({
          label: t('pages.users.manageDevices'),
          path: location.pathname,
          icon: <UserCog className="h-4 w-4" />,
        });
      }
    } else if (pathSegments[0] === 'devices') {
      // Devices routes
      breadcrumbs.push({
        label: t('pages.devices.title'),
        path: '/devices',
        icon: <Smartphone className="h-4 w-4" />,
      });

      if (pathSegments[1] === 'new') {
        breadcrumbs.push({
          label: t('pages.devices.createNew'),
          path: location.pathname,
          icon: <SmartphoneIcon className="h-4 w-4" />,
        });
      } else if (pathSegments[1] === 'edit' && pathSegments[2]) {
        breadcrumbs.push({
          label: t('pages.devices.editDevice'),
          path: location.pathname,
          icon: <Edit className="h-4 w-4" />,
        });
      } else if (pathSegments[2] === 'users') {
        breadcrumbs.push({
          label: t('pages.devices.manageUsers'),
          path: location.pathname,
          icon: <Users2 className="h-4 w-4" />,
        });
      } else if (pathSegments[1] && !['edit', 'new'].includes(pathSegments[1])) {
        // Device detail page
        breadcrumbs.push({
          label: t('pages.devices.editDevice'),
          path: location.pathname,
          icon: <Edit className="h-4 w-4" />,
        });
      }
    } else if (pathSegments[0] === 'operations') {
      // Operations routes
      breadcrumbs.push({
        label: t('pages.mainMenu.operationalDashboard.title'),
        path: '/operations',
        icon: <BarChart3 className="h-4 w-4" />,
      });

      if (pathSegments[1] === 'device' && pathSegments[2]) {
        if (pathSegments[3] === 'operation' && pathSegments[4] && pathSegments[5] === 'lectures') {
          breadcrumbs.push({
            label: t('pages.operations.deviceOperations'),
            path: `/operations/device/${pathSegments[2]}`,
            icon: <Smartphone className="h-4 w-4" />,
          });
          breadcrumbs.push({
            label: t('pages.operations.operationLectures'),
            path: location.pathname,
            icon: <FileInput className="h-4 w-4" />,
          });
        } else if (pathSegments[3] === 'all-lectures') {
          breadcrumbs.push({
            label: t('pages.operations.allDeviceLectures'),
            path: location.pathname,
            icon: <FileInput className="h-4 w-4" />,
          });
        } else {
          breadcrumbs.push({
            label: t('pages.operations.deviceOperations'),
            path: location.pathname,
            icon: <Smartphone className="h-4 w-4" />,
          });
        }
      } else if (pathSegments[1] === 'device' && pathSegments[2] === 'all-lectures') {
        breadcrumbs.push({
          label: t('pages.operations.totalLectures'),
          path: location.pathname,
          icon: <FileInput className="h-4 w-4" />,
        });
      }
    } else if (pathSegments[0] === 'lectures' && pathSegments[1] === 'insert') {
      breadcrumbs.push({
        label: t('pages.mainMenu.lectureInsert.title'),
        path: location.pathname,
        icon: <FileInput className="h-4 w-4" />,
      });
    } else if (pathSegments[0] === 'lectures' && pathSegments[1] === 'bulk-insert') {
      breadcrumbs.push({
        label: 'Inserción Masiva de Lecturas',
        path: location.pathname,
        icon: <Package className="h-4 w-4" />,
      });
    } else if (pathSegments[0] === 'lectures' && pathSegments[1] === 'import') {
      breadcrumbs.push({
        label: 'Importar lecturas por archivo',
        path: location.pathname,
        icon: <FileUp className="h-4 w-4" />,
      });
    } else if (pathSegments[0] === 'dashboard') {
      breadcrumbs.push({
        label: t('common.dashboard'),
        path: '/dashboard',
        icon: <BarChart3 className="h-4 w-4" />,
      });
    } else if (pathSegments[0] === 'platform') {
      breadcrumbs.push({
        label: 'Gestion de plataforma',
        path: '/platform',
        icon: <Settings className="h-4 w-4" />,
      });
      if (pathSegments[1] === 'event-types') {
        breadcrumbs.push({
          label: 'Gestión de tipos de eventos',
          path: '/platform/event-types',
          icon: <Settings className="h-4 w-4" />,
        });
        if (pathSegments[2] === 'new') {
          breadcrumbs.push({
            label: 'Crear tipo de evento',
            path: '/platform/event-types/new',
            icon: <Settings className="h-4 w-4" />,
          });
        } else if (pathSegments[2] === 'edit' && pathSegments[3]) {
          breadcrumbs.push({
            label: 'Editar tipo de evento',
            path: location.pathname,
            icon: <Edit className="h-4 w-4" />,
          });
        }
      }
    } else if (pathSegments[0] === 'menu') {
      // Menu page - only show home
      return breadcrumbs.slice(0, 1);
    }

    return breadcrumbs;
  };

  const handleNavigation = async (crumb: BreadcrumbItem) => {
    await navigateWithTokenCheck(navigate, crumb.path);
  };

  const breadcrumbs = getBreadcrumbs();

  // Always show breadcrumbs (at least Home)
  return (
    <nav className="flex" aria-label="Breadcrumb">
      <ol className="flex items-center space-x-1">
        {breadcrumbs.map((crumb, index) => (
          <li key={`${crumb.path}-${index}`} className="flex items-center">
            {index > 0 && <ChevronRight className="h-4 w-4 text-brand-gray-light mx-1" />}
            <button
              onClick={() => handleNavigation(crumb)}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-sm font-medium transition-colors duration-200 ${
                index === breadcrumbs.length - 1
                  ? 'text-brand-orange'
                  : 'text-white hover:bg-orange-50 hover:text-brand-orange'
              }`}
            >
              {crumb.icon}
              {crumb.label}
            </button>
          </li>
        ))}
      </ol>
    </nav>
  );
};
